import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import { toastr } from 'react-redux-toastr';
import {
    Grid,
    AppBar,
    Button,
    Divider,
    IconButton,
    Dialog,
    Drawer,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import {
    MoreHoriz,
} from '@material-ui/icons';
import {
    ShiftlineSelectionModals,
    EmployeeShiftLineTemplateAction,
    BidProcessType,
    ShiftlineSelectionTableType,
    AnchorDay,
    ScheduleSource,
    PeriodType,
    EmployeeReportType,
    ShiftlineFilterSelectionTabs,
} from '@enums/index';
import {
    saveBidSelectionDraft,
    saveBidSelection,
    saveBidSelectionTemplate,
    loadBidSelectionTemplate,
    declineBidSelection,
} from '../store/bidProfile';
import {
    handleAutoOrderSelected,
} from '../../store/bidSelection';
import {
    ShiftlineSelectionHeader,
    ShiftlineSelectionConfirmation,
    BidActions,
    EmployeeReport,
    useEmployeeInfoMobile,
} from '../../components';
import ShiftlineFilterSelectionBar from '../components/ShiftlineFilterSelectionBar';
import NoSchedulesAvailable from '../components/NoSchedulesAvailable';
import ShiftLineSelectionTable from '../components/ShiftLineSelectionTable';
import ShiftLineSelectionMobile from '../components/ShiftLineSelectionMobile';
// eslint-disable-next-line no-unused-vars
import './ShiftlineSelection.scss';
import { DAY_COLUMNS_COUNT } from '../../Manager/BidProfileEdit/ScheduleSelection/ShiftlineTable/ShiftlineTable';

function ShiftlineSelection() {
    const { t: tEmployee } = useTranslation('shiftBidding', { keyPrefix: 'employee' });
    const dispatch = useDispatch();
    const navigate = useNavigate();

    /** @type {ShiftBidding.EmployeeStore} */
    // @ts-ignore
    const { employeeBidProfile, employeeBidSelection } = useSelector((state) => state);
    const {
        anchorDay,
        bidProcessType,
        dayCount,
        isRebidAllowed,
        items,
        name,
        periodType,
        profileId,
        sequentialSchedule,
        weeklyHours,
        weeklyHoursByDefault,
        lockedEmployeeIds,
        isGroupShiftlinesWithSameSchedules,
        employeeRankFullNames,
    } = employeeBidProfile;

    const {
        employeeSelections,
        selectionFilters
    } = employeeBidSelection;

    const [EmployeeInfoMobile] = useEmployeeInfoMobile(employeeBidProfile);

    const isUnselectedTab = selectionFilters.selectedTab === ShiftlineFilterSelectionTabs.Unselected;
    const isPreferential = bidProcessType === BidProcessType.Preferential;
    const isSequential = bidProcessType === BidProcessType.Sequential;

    const [autoNumberingType, setAutoNumberingType] = useState('');
    const [windowWidth, setWindowWidth] = useState(0);
    const [showModal, setShowModal] = useState(ShiftlineSelectionModals.None);
    const [showActionButton, setShowActionButton] = useState(false);
    const [initialSelection, setInitialSelection] = useState(employeeSelections);
    const [showReport, setShowReport] = useState(ShiftlineSelectionModals.None);
    const isSelectionDirty = !isEqual(initialSelection, employeeSelections);
    const employeeFilters = employeeRankFullNames?.map((employee) => ({
        employeeId: employee.employeeId,
        name: employee.fullName,
        rank: employee.rank,
    }));

    useEffect(() => {
        function handleResize() {
            setWindowWidth(window.innerWidth);
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleClose = () => {
        if (employeeSelections.length === 0) {            
            toastr.error('Saving selection draft', 'Please choose at least one selection');
            setShowModal(ShiftlineSelectionModals.None);
            return;
        }
        dispatch(saveBidSelectionDraft(employeeBidProfile.profileId, employeeSelections));
        setInitialSelection(employeeSelections);
        setShowModal(ShiftlineSelectionModals.None);
        navigate('/Employee');
    };
    const handleSubmit = () => {
        dispatch(saveBidSelection(employeeBidProfile.profileId, employeeSelections));
        setInitialSelection(employeeSelections);
        setShowModal(ShiftlineSelectionModals.None);
    };
    const handleDecline = () => {
        dispatch(declineBidSelection(employeeBidProfile.profileId));
        setInitialSelection([]);
        setShowModal(ShiftlineSelectionModals.None);
    };
    const handleSaveTemplate = () => {
        dispatch(saveBidSelectionTemplate(employeeBidProfile.profileId, employeeSelections));
        setShowModal(ShiftlineSelectionModals.None);
    };
    const handleLoadTemplate = () => {
        dispatch(loadBidSelectionTemplate(employeeBidProfile.profileId, employeeBidSelection.items, isGroupShiftlinesWithSameSchedules));
        setShowModal(ShiftlineSelectionModals.None);
    };
    const handleAutoOrder = () => {
        dispatch(
            handleAutoOrderSelected(
                employeeBidSelection,
                autoNumberingType,
                employeeBidSelection.filteredItems,
                isGroupShiftlinesWithSameSchedules
            )
        );
        setShowModal(ShiftlineSelectionModals.None);
    };

    const handleBackWithValidation = () => {
        if (employeeSelections.length === 0) {
            toastr.error('Saving selection draft', 'Please choose at least one selection');
            setShowModal(ShiftlineSelectionModals.None);
            return;
        }
        navigate('/Employee');
    };

    /**
     * @param {{label: string, value: number, disabled: boolean}} option
     */
    const handleTemplateAction = (option) => {
        if (option.value === EmployeeShiftLineTemplateAction.Save) {
            setShowModal(ShiftlineSelectionModals.SaveTemplate);
        } else if (option.value === EmployeeShiftLineTemplateAction.Load) {
            setShowModal(ShiftlineSelectionModals.LoadTemplate);
        }
    };

    const isMobile = windowWidth < 992;

    return (
        <>
            {
                isMobile ? (
                    <div className="shifts-list-container">
                        <div className="topMenuBar">
                            <div className="topMenuFirstRow">
                                <div className="sl-leftSide">
                                    <IconButton
                                        onClick={isSelectionDirty ? () => setShowModal(ShiftlineSelectionModals.Back) : () => handleBackWithValidation()}
                                    >
                                        <FontAwesomeIcon icon={faArrowLeft} />
                                    </IconButton>
                                    <p className="headline-h-700 sl-bid-name">{employeeBidProfile.name}</p>
                                </div>
                                <div className="sl-rightSide">
                                    <div className="action-icons-container">
                                        <EmployeeInfoMobile />
                                        <Button onClick={() => setShowActionButton(true)}>
                                            <MoreHoriz />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            <div className="filterSelectionTab">
                                <ShiftlineFilterSelectionBar
                                    isSelectedButtonHidden={!isPreferential}
                                    showAutoOrderButton={isPreferential}
                                    setShowAutoOrderModal={(/** @type {boolean} */ isSetVisible) => (
                                        isSetVisible
                                            ? setShowModal(ShiftlineSelectionModals.AutoNumbering)
                                            : setShowModal(ShiftlineSelectionModals.None)
                                    )}
                                    periodType={periodType}
                                    dayCount={dayCount}
                                    weeklyHours={false} // TODO - 12-13 kathy - add weekly hours to mobile
                                    weeklyHoursByDefault={false}// TODO - 12-13 kathy - add weekly hours to mobile
                                />
                            </div>
                        </div>
                        {items.length ? (
                            <ShiftLineSelectionMobile
                                isMultipleSelection={isPreferential}
                                daysPerPeriod={DAY_COLUMNS_COUNT}
                            />
                        ) : (
                            <NoSchedulesAvailable />
                        )}
                        {isPreferential && (
                            <AppBar
                                position="fixed"
                                className="sl-fixed-buttons-container"
                            >
                                <div
                                    className="sl-select-button-container"
                                    hidden={!isUnselectedTab}
                                >
                                    <button
                                        type="button"
                                        onClick={() => setShowModal(ShiftlineSelectionModals.AutoNumbering)}
                                    >
                                        Select in Order
                                    </button>
                                </div>
                            </AppBar>
                        )}
                        <Drawer
                            className="sl-actions-buttons-container"
                            anchor="bottom"
                            open={showActionButton}
                            onClose={() => setShowActionButton(false)}
                        >
                            <BidActions
                                bidProfile={employeeBidProfile}
                                employeeBidSelection={employeeBidSelection}
                                setShowReport={setShowReport}
                                setShowModal={setShowModal}
                                handleTemplateAction={handleTemplateAction}
                                isOnBehalf={false}
                                isHideTemplateActionsInUnselectedTab={false}
                            />
                        </Drawer>
                    </div>
                ) : (
                    <>
                        <ShiftlineSelectionHeader
                            isOnBehalf={false}
                            isSelectionDirty={isSelectionDirty}
                            setShowModal={setShowModal}
                            setShowReport={setShowReport}
                            handleBack={handleBackWithValidation}
                            handleTemplateAction={handleTemplateAction}
                        />
                        <Divider />
                        <Grid
                            container
                            className="sl-selection-filters"
                        >
                            <ShiftlineFilterSelectionBar
                                isSelectedButtonHidden={!isPreferential}
                                showAutoOrderButton={isPreferential}
                                setShowAutoOrderModal={(/** @type {boolean} */ isSetVisible) => (
                                    isSetVisible
                                        ? setShowModal(ShiftlineSelectionModals.AutoNumbering)
                                        : setShowModal(ShiftlineSelectionModals.None)
                                )}
                                periodType={periodType}
                                dayCount={dayCount}
                                weeklyHours={weeklyHours}
                                weeklyHoursByDefault={weeklyHoursByDefault}
                            />
                        </Grid>
                        {
                            items.length ? (
                                <ShiftLineSelectionTable
                                    isMultipleSelection={isPreferential}
                                    daysPerPeriod={DAY_COLUMNS_COUNT}
                                    type={ShiftlineSelectionTableType.EditSelection}
                                    externalStoreData={{
                                        filteredItems: [],
                                        shiftsDictionary: {},
                                        anchorDay: AnchorDay.Sunday,
                                        scheduleSource: ScheduleSource.ExcelFile,
                                        dayCount: 0,
                                        periodType: PeriodType.Weeks,
                                    }}
                                />
                            ) : (
                                <NoSchedulesAvailable />
                            )
                        }
                    </>
                )
            }
            <Dialog
                open={showModal !== ShiftlineSelectionModals.None}
                onClose={() => setShowModal(ShiftlineSelectionModals.None)}
            >
                <ShiftlineSelectionConfirmation
                    showModal={showModal}
                    templateToLoadName={name}
                    autoNumberingType={autoNumberingType}
                    setShowModal={setShowModal}
                    handleSaveTemplate={handleSaveTemplate}
                    handleLoadTemplate={handleLoadTemplate}
                    handleSaveAsDraft={handleClose}
                    handleSubmit={handleSubmit}
                    handleDecline={handleDecline}
                    setAutoNumberingType={setAutoNumberingType}
                    handleAutoOrder={handleAutoOrder}
                    handleBack={handleBackWithValidation}
                    t={tEmployee}
                />
            </Dialog>
            <EmployeeReport
                profileId={profileId}
                employeeReport={showReport}
                onClose={() => setShowReport(EmployeeReportType.None)}
                dataviewEmployeeData={[]}
                filteredDataviewEmployeeData={[]}
                dataviewFields={[]}
                anchorDay={anchorDay}
                isRebid={isRebidAllowed}
                showScheduledDateAndTime={isSequential}
                sequentialSchedule={sequentialSchedule}
                dayCount={dayCount}
                isPeriodTypeDay={periodType === PeriodType.Days}
                lockedEmployeeIds={lockedEmployeeIds}
                employeeFilters={employeeFilters}
            />
        </>
    );
}

export default ShiftlineSelection;
